import React from 'react'
import { GaugeComponent } from 'react-gauge-component'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material'
import { RadialGaugeProps } from '../../types'
import { useThemeColors } from '../../hooks/useThemeColors'

const RadialGauge: React.FC<RadialGaugeProps> = ({
  value = 0,
  maxValue = 100,
  minValue = 0,
  loading = false,
  error = null
}) => {
  const colors = useThemeColors()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography
        variant='h5'
        gutterBottom
        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Performance Gauge
      </Typography>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.background.paper,
          minHeight: 300
        }}>
        {loading && (
          <>
            <CircularProgress />
            <Typography variant='body1' sx={{ mt: 2 }}>
              Loading gauge data...
            </Typography>
          </>
        )}

        {error && (
          <Alert severity='error' sx={{ width: '100%' }}>
            Failed to load gauge data: {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <GaugeComponent
              arc={{
                subArcs: [
                  { limit: 20, color: '#EA4228', showTick: true },
                  { limit: 40, color: '#F58B19', showTick: true },
                  { limit: 60, color: '#F5CD19', showTick: true },
                  { limit: 100, color: '#5BE12C', showTick: true }
                ]
              }}
              value={value}
              minValue={minValue}
              maxValue={maxValue}
              pointer={{ type: 'blob', animationDelay: 0 }}
              style={{
                width: isMobile ? '250px' : '300px',
                maxWidth: '100%'
              }}
            />
            <Typography
              variant='h6'
              sx={{
                mt: 2,
                textAlign: 'center',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}>
              Current Value: {value} / {maxValue}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{
                textAlign: 'center',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}>
              Performance Level:{' '}
              {value < 20 ? 'Low' : value < 60 ? 'Medium' : 'High'}
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  )
}

export default RadialGauge
